import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, map, catchError, of } from "rxjs";
import { environment } from "src/environments/environment";
import { ApiUrl } from "./api.url";
import { ExternAdapter } from "../models/adapter/extern.adapter";
import { Extern } from "../models/extern.model";
import { CompanyAdapter } from "../models/adapter/company.adapter";

@Injectable({
    providedIn: 'root'
})
export class ExternService {

    constructor(
        protected http: HttpClient,
        protected externAdapter: ExternAdapter,
        protected companyAdapter: CompanyAdapter
    ) { }

    public getAll(): Observable<Extern[] | null> {
        return this.http.get(`${environment.webappBaseUrl}${ApiUrl.GET_EXTERN}`).pipe(
            map((externs: any) => {
                if (externs) {
                    return externs.map((item: any) => this.externAdapter.adapt(item));
                }
                return [];
            }),
            catchError(err => of(null)),
        );
    }

    public getCoops(): Observable<Extern[] | null> {
        return this.http.get(`${environment.webappBaseUrl}${ApiUrl.GET_EXTERN}`).pipe(
            map((externs: any) => {
                if (externs) {
                    return externs
                        .map((item: any) => this.externAdapter.adapt(item))
                        .filter((extern: Extern) => extern.isCoop);
                }
                return [];
            }),
            catchError(err => of(null)),
        );
    }

    public getOne(externId: number): Observable<Extern | null> {
        return this.http.get(`${environment.webappBaseUrl}${ApiUrl.GET_ONE_EXTERN}`.replace(':id', externId.toString())).pipe(
            map((item: any) => {
                if (item) {
                    const companies = (item.companies) ? item.companies.map((company: any) => this.companyAdapter.adapt(company, [])) : null;
                    return this.externAdapter.adapt(item, companies);
                }
                return null;
            }),
            catchError(err => of(null)),
        );
    }
    
    public add(data: FormData): Observable<Extern | null> {
        return this.http.post(`${environment.webappBaseUrl}${ApiUrl.POST_EXTERN}`, data).pipe(
            map((item: any) => {
                if (item) {
                    return this.externAdapter.adapt(item);
                }
                return null;
            }),
            catchError(err => of(null)),
        );
    }

    public update(externId: number, data: FormData): Observable<Extern | null> {
        return this.http.put(`${environment.webappBaseUrl}${ApiUrl.PUT_EXTERN}`.replace(':id', externId.toString()), data).pipe(
            map((item: any) => {
                if (item) {
                    return this.externAdapter.adapt(item);
                }
                return null;
            }),
            catchError(err => of(null)),
        );
    }

    public delete(externId: number): Observable<boolean> {
        return this.http.delete(`${environment.webappBaseUrl}${ApiUrl.DELETE_EXTERN}`.replace(':id', externId.toString())).pipe(
            map((response: any) => {
                return true;
            }),
            catchError(err => of(false)),
        );
    }

    public linkCompany(externId: number, companyId: number): Observable<Extern | null> {
        const url = `${environment.webappBaseUrl}${ApiUrl.PUT_LINK_EXTERN}`
            .replace(':externId', externId.toString())
            .replace(':companyId', companyId.toString());
        return this.http.put(url, null).pipe(
            map((item: any) => {
                if (item) {
                    return this.externAdapter.adapt(item);
                }
                return null;
            }),
            catchError(err => of(null)),
        );
    }

    public unlinkCompany(externId: number, companyId: number): Observable<Extern | null> {
        const url = `${environment.webappBaseUrl}${ApiUrl.DELETE_LINK_EXTERN}`
            .replace(':externId', externId.toString())
            .replace(':companyId', companyId.toString());
        return this.http.delete(url).pipe(
            map((item: any) => {
                if (item) {
                    return this.externAdapter.adapt(item);
                }
                return null;
            }),
            catchError(err => of(null)),
        );
    }

    public reloadApi(externId: number): Observable<Extern | null> {
        return this.http.post(`${environment.webappBaseUrl}${ApiUrl.POST_RELOAD_API}`.replace(':id', externId.toString()), null).pipe(
            map((item: any) => {
                if (item) {
                    return this.externAdapter.adapt(item);
                }
                return null;
            }),
            catchError(err => of(null)),
        );
    }

    public getCompanyExterns(companyId: number): Observable<{ externs: Extern[], coop: Extern | null }> {
        return this.http.get(`${environment.webappBaseUrl}${ApiUrl.GET_COMPANY_EXTERNS}`.replace(':companyId', companyId.toString())).pipe(
            map((response: any) => {
                return {
                    externs: response.externs ? response.externs.map((item: any) => this.externAdapter.adapt(item)) : [],
                    coop: response.coop ? this.externAdapter.adapt(response.coop) : null
                };
            }),
            catchError(err => of({ externs: [], coop: null })),
        );
    }
}